/**
 * Landing — Fortune-500-clean redesign (Feb 2026).
 *
 *  Directive from product: strip the homepage to LISTENERSHIP + SIGN-UP only.
 *  Everything else (features, pricing, comparison, confidence, etc.) moves to
 *  /product. Fortune-500 aesthetic = generous whitespace, restrained palette,
 *  one hero photo, one big number, one primary CTA.
 *
 *  Sections:
 *    1. Compact NavBar (Listen · Sign in · Get started free)
 *    2. HERO — full-viewport photo bg + geo-aware listener count
 *    3. Consolidated LIVE PICKER — 6 stations + "explore all" link
 *    4. TRENDING STRIP — top-5 stations in visitor's country this hour
 *    5. SUBSCRIBE BAND — one clean CTA for broadcasters/advertisers
 *    6. FOOTER — I-Assist Pro Limited · +233 20 855 3679
 */
import { useNavigate, Link } from "react-router-dom";
import { useEffect, useState } from "react";
import axios from "axios";
import { NavBar } from "@/components/NavBar";
import { WaveMark } from "@/components/WaveMark";
import { usePlayerCtx } from "@/context/PlayerContext";
import { T } from "@/lib/theme";
import { labelFor } from "@/lib/labels";
import { trackClick } from "@/lib/telemetry";

const API_BASE = (process.env.REACT_APP_BACKEND_URL || "") + "/api";

// Curated Unsplash hero — young woman with headphones dancing & smiling
// (photo by Vitaly Gariev / Unsplash, verified URL). Matches directive #6.
const HERO_PHOTO =
  "https://images.unsplash.com/photo-1758273240322-47b40a898d0e?fm=jpg&q=80&w=1920&auto=format&fit=crop&ixlib=rb-4.1.0";

export default function Landing() {
  const nav = useNavigate();
  const player = usePlayerCtx();

  const [stats, setStats] = useState(() => {
    try {
      const c = localStorage.getItem("wsp_landing_stats");
      if (c) return JSON.parse(c);
    } catch { /* ignore */ }
    return null;
  });
  const [grid, setGrid] = useState(() => {
    try {
      const c = localStorage.getItem("wsp_home_grid_cache");
      if (c) return JSON.parse(c);
    } catch { /* ignore */ }
    return null;
  });
  const [ytVideos, setYtVideos] = useState([]);

  useEffect(() => {
    let stop = false;
    const loadStats = () =>
      axios.get(`${API_BASE}/public/landing-stats`)
        .then((r) => {
          if (stop) return;
          setStats(r.data);
          try { localStorage.setItem("wsp_landing_stats", JSON.stringify(r.data)); } catch { /* ignore */ }
        })
        .catch(() => { /* silent — hero shows a graceful placeholder */ });
    const loadGrid = () =>
      // Force Ghana focus per product directive #4 — landing picker always
      // shows Ghana stations first even if the visitor is browsing from abroad.
      axios.get(`${API_BASE}/public/home-grid`, { params: { country: "Ghana" } })
        .then((r) => {
          if (stop) return;
          setGrid(r.data);
          try { localStorage.setItem("wsp_home_grid_cache", JSON.stringify(r.data)); } catch { /* ignore */ }
        })
        .catch(() => { /* silent */ });
    loadStats(); loadGrid();
    // YouTube trending — 30-min server cache, so no need to refetch often.
    axios.get(`${API_BASE}/public/youtube-trending`, { params: { region: "GH" } })
      .then((r) => { if (!stop) setYtVideos(r.data.videos || []); })
      .catch(() => { /* silent — section auto-hides if empty */ });
    const t = setInterval(() => { loadStats(); loadGrid(); }, 60000);
    return () => { stop = true; clearInterval(t); };
  }, []);

  const country = stats?.display_country || "Ghana";
  const listeners = stats?.listeners_now ?? 0;
  // Phase 1 — hero copy carries an "online" qualifier so the count is
  // scope-honest. When the representative layer ships (flag ON) this
  // becomes an empty string and the headline reads plainly.
  const [phase, setPhase] = useState({ scope_qualifier: "online", phase_label: "phase1", hero_pill_variant: "coverage_educate" });
  const [coverage, setCoverage] = useState(null);
  useEffect(() => {
    axios.get(`${API_BASE}/public/phase`)
      .then((r) => setPhase(r.data)).catch(() => {});
    axios.get(`${API_BASE}/public/crawler`)
      .then((r) => setCoverage(r.data?.coverage || null)).catch(() => {});
  }, []);
  const qualifier = phase.scope_qualifier ? ` ${phase.scope_qualifier}` : "";
  const trending = stats?.trending || [];
  // Ghana focus (directive #4): filter picker to Ghana + streamable + measured.
  // We prefer measured stations but backfill with unmeasured Ghana streams so
  // there's always something to press play on.
  const ghanaStations = (grid?.stations || []).filter(
    (s) => s.has_stream && (s.country || "").toLowerCase() === "ghana"
  );
  const measuredGhana = ghanaStations.filter((s) => s.measurement_state === "measured");
  const pickerStations = (measuredGhana.length >= 6 ? measuredGhana : ghanaStations).slice(0, 6);

  return (
    <div data-testid="landing-page" style={{
      fontFamily: "'Inter',system-ui,sans-serif",
      background: "#fff", color: T.navy, minHeight: "100vh",
    }}>
      <style>{`
        *{box-sizing:border-box}body{margin:0}
        @keyframes wsFadeUp { from{opacity:0;transform:translateY(12px)} to{opacity:1;transform:translateY(0)} }
        @keyframes wsPulse   { 0%{box-shadow:0 0 0 0 ${T.green}80} 70%{box-shadow:0 0 0 14px ${T.green}00} 100%{box-shadow:0 0 0 0 ${T.green}00} }
        @keyframes wsTick    { from{opacity:.3} to{opacity:1} }
        @keyframes wsHeroDrift { 0%{transform:translate3d(0,0,0)} 50%{transform:translate3d(-22px,-10px,0)} 100%{transform:translate3d(0,0,0)} }
        @keyframes wsHeroWave { 0%{stroke-dashoffset:0} 100%{stroke-dashoffset:-280} }
        @keyframes wsHeroPing { 0%{r:2;opacity:0.9} 80%{r:16;opacity:0} 100%{r:16;opacity:0} }
        .ws-fade-up { animation: wsFadeUp .8s ease-out both; }
        .ws-fade-up-delay-1 { animation: wsFadeUp .8s .12s ease-out both; }
        .ws-fade-up-delay-2 { animation: wsFadeUp .8s .24s ease-out both; }
        .ws-live-dot { display:inline-block; width:8px; height:8px; border-radius:50%; background:${T.green}; animation: wsPulse 1.8s ease-out infinite; }
        .ws-hover-lift { transition: transform .2s ease, box-shadow .2s ease; }
        .ws-hover-lift:hover { transform: translateY(-2px); box-shadow: 0 12px 30px rgba(10,22,40,.08); }
        .ws-num { font-family:'Space Grotesk','Inter',sans-serif; font-feature-settings:'tnum' 1,'lnum' 1; letter-spacing:-0.02em; }
        .ws-hero-waves { position:absolute; inset:0; pointer-events:none; z-index:0; overflow:hidden; }
        .ws-hero-waves svg { position:absolute; inset:0; width:100%; height:100%; will-change:transform; }
        .ws-hero-waves .drift { animation: wsHeroDrift 22s ease-in-out infinite; }
        .ws-hero-waves .wave-line { stroke-dasharray: 6 8; animation: wsHeroWave 9s linear infinite; }
        .ws-hero-waves .wave-line.slow { animation-duration: 14s; }
        .ws-hero-waves .wave-line.fast { animation-duration: 6.5s; }
        .ws-hero-waves .ping { transform-origin: center; animation: wsHeroPing 3.4s ease-out infinite; }
        .ws-hero-waves .ping.p2 { animation-delay: 1.1s; }
        .ws-hero-waves .ping.p3 { animation-delay: 2.2s; }
        @media (prefers-reduced-motion: reduce) {
          .ws-hero-waves .drift, .ws-hero-waves .wave-line, .ws-hero-waves .ping { animation: none; }
        }
      `}</style>

      <NavBar dark />

      {/* ────────────────────────────────────────────────────────────────
          1. HERO — photo + geo-aware listener count
         ──────────────────────────────────────────────────────────────── */}
      {/* App-level <main id="main"> wraps AppRoutes (App.js:154); no nested
          <main> here (invalid HTML). Just group the visible content. */}
      <div data-testid="landing-main">
      <section data-testid="hero" style={{
        position: "relative", minHeight: "82vh", overflow: "hidden",
        background: `linear-gradient(180deg, rgba(10,22,40,0.72) 0%, rgba(10,22,40,0.88) 100%), url('${HERO_PHOTO}') center/cover no-repeat`,
        color: "#fff",
      }}>
        {/* Moving wave / West-Africa ping backdrop — brand signature motion.
            Sits above the photo but below the copy (z:0 vs z:1). Aria-hidden
            because it's decorative; honours prefers-reduced-motion. */}
        <div className="ws-hero-waves" aria-hidden="true" data-testid="hero-waves">
          <svg className="drift" viewBox="0 0 1440 600" preserveAspectRatio="xMidYMid slice">
            <defs>
              <linearGradient id="wsHeroWaveG" x1="0" y1="0" x2="1" y2="0">
                <stop offset="0%" stopColor="#00D4FF" stopOpacity="0" />
                <stop offset="40%" stopColor="#00D4FF" stopOpacity="0.7" />
                <stop offset="60%" stopColor="#00C896" stopOpacity="0.7" />
                <stop offset="100%" stopColor="#00C896" stopOpacity="0" />
              </linearGradient>
              <pattern id="wsHeroDot" x="0" y="0" width="36" height="36" patternUnits="userSpaceOnUse">
                <circle cx="2" cy="2" r="1.2" fill="#ffffff" opacity="0.08" />
              </pattern>
            </defs>
            <rect width="1440" height="600" fill="url(#wsHeroDot)" />
            <path className="wave-line" stroke="url(#wsHeroWaveG)" strokeWidth="2" fill="none"
              d="M-50 320 Q120 240 290 320 T620 320 T950 320 T1280 320 T1610 320" />
            <path className="wave-line slow" stroke="url(#wsHeroWaveG)" strokeWidth="1.6" fill="none" opacity="0.55"
              d="M-50 400 Q140 320 300 400 T630 400 T960 400 T1290 400 T1620 400" />
            <path className="wave-line fast" stroke="url(#wsHeroWaveG)" strokeWidth="1.4" fill="none" opacity="0.4"
              d="M-50 240 Q140 180 300 240 T630 240 T960 240 T1290 240 T1620 240" />
            <g fill="#00C896">
              <circle className="ping" cx="430" cy="440" r="2" />
              <circle cx="430" cy="440" r="3" />
            </g>
            <g fill="#00D4FF">
              <circle className="ping p2" cx="820" cy="200" r="2" />
              <circle cx="820" cy="200" r="3" />
            </g>
            <g fill="#00C896">
              <circle className="ping p3" cx="1120" cy="380" r="2" />
              <circle cx="1120" cy="380" r="3" />
            </g>
          </svg>
        </div>

        <div style={{
          maxWidth: 1180, margin: "0 auto", padding: "80px 24px 96px",
          display: "grid", gridTemplateColumns: "minmax(0, 1fr)", gap: 24, alignItems: "center",
          position: "relative", zIndex: 1,
        }}>
          <div style={{ maxWidth: 780 }}>
            <div data-testid="hero-live-pill" className="ws-fade-up" style={{
              display: "inline-flex", alignItems: "center", gap: 10,
              padding: "6px 14px", borderRadius: 999,
              background: "rgba(0,200,150,0.14)", border: "1px solid rgba(0,200,150,0.35)",
              color: T.green, fontSize: 11, fontWeight: 700, letterSpacing: "0.14em",
              textTransform: "uppercase",
            }}>
              <span className="ws-live-dot" /> Live from {country}
            </div>

            <h1 data-testid="hero-heading" className="ws-fade-up-delay-1" style={{
              fontSize: "clamp(40px, 6vw, 76px)", fontWeight: 800,
              lineHeight: 1.02, letterSpacing: "-0.03em",
              margin: "22px 0 18px", color: "#fff",
            }}>
              Right now,{" "}
              <span data-testid="hero-count" className="ws-num" style={{ color: T.green }}>
                {listeners > 0 ? listeners.toLocaleString() : "—"}
              </span>{" "}
              live connections<br />
              are listening{qualifier} in {country}.
            </h1>

            <p data-testid="hero-sub" className="ws-fade-up-delay-2" style={{
              fontSize: 18, lineHeight: 1.6, color: "rgba(255,255,255,0.8)",
              maxWidth: 620, margin: "0 0 36px",
            }}>
              {phase.phase_label === "phase1"
                ? "Live digital connection count from station servers & platform APIs. Ghana's top FM stations, free — pick one below and press play. No signup, no ads on this page."
                : "Ghana's top FM stations, live and free — pick one below and press play. No signup, no ads on this page."}
            </p>

            <div className="ws-fade-up-delay-2" style={{ display: "flex", gap: 14, flexWrap: "wrap" }}>
              <button data-testid="hero-cta-listen"
                onClick={() => document.getElementById("live-picker")?.scrollIntoView({ behavior: "smooth" })}
                style={{
                  background: T.green, color: "#fff", border: "none",
                  padding: "16px 30px", borderRadius: 999, cursor: "pointer",
                  fontSize: 15, fontWeight: 700, fontFamily: "inherit",
                  boxShadow: "0 12px 30px rgba(0,200,150,0.4)",
                }}>
                Start listening →
              </button>
              <button data-testid="hero-cta-register"
                onClick={() => nav("/register")}
                style={{
                  background: "transparent", color: "#fff",
                  border: "1px solid rgba(255,255,255,0.35)",
                  padding: "16px 30px", borderRadius: 999, cursor: "pointer",
                  fontSize: 15, fontWeight: 600, fontFamily: "inherit",
                }}>
                Get started free
              </button>
            </div>

            <div data-testid="hero-mini-stats" className="ws-fade-up-delay-2" style={{
              marginTop: 42, display: "flex", gap: 40, flexWrap: "wrap",
              color: "rgba(255,255,255,0.75)", fontSize: 12,
            }}>
              <span><b style={{ color: "#fff" }} className="ws-num">{stats?.stations_online ?? "—"}</b> stations publishing live counts</span>
              {coverage && coverage.stations_known > 0 && phase.hero_pill_variant === "convert_operator" && (
                <Link
                  to="/streams"
                  data-testid="hero-coverage-pill"
                  data-variant="convert_operator"
                  onClick={() => trackClick("landing.hero_pill.convert_operator")}
                  style={{
                    display: "inline-flex", alignItems: "center", gap: 8,
                    padding: "5px 12px", borderRadius: 999,
                    background: "rgba(0,200,150,0.16)",
                    border: "1px solid rgba(0,200,150,0.42)",
                    color: T.greenAccent, fontSize: 11,
                    fontWeight: 700, letterSpacing: "0.04em",
                    textDecoration: "none", cursor: "pointer",
                  }}
                  title="Free measurement for licensed broadcasters — click to apply"
                >
                  <span style={{ width: 6, height: 6, borderRadius: "50%", background: T.greenAccent }} />
                  <span>Own a station? Get measured free →</span>
                </Link>
              )}
              {coverage && coverage.stations_known > 0 && phase.hero_pill_variant !== "convert_operator" && (
                <Link
                  to="/crawler"
                  data-testid="hero-coverage-pill"
                  data-variant="coverage_educate"
                  onClick={() => trackClick("landing.hero_pill.coverage_educate")}
                  style={{
                    display: "inline-flex", alignItems: "center", gap: 8,
                    padding: "5px 12px", borderRadius: 999,
                    background: "rgba(255,255,255,0.08)",
                    border: "1px solid rgba(255,255,255,0.18)",
                    color: "rgba(255,255,255,0.85)", fontSize: 11,
                    fontWeight: 600, letterSpacing: "0.04em",
                    textDecoration: "none", cursor: "pointer",
                  }}
                  title="How we measure — click for full policy"
                >
                  <span style={{ width: 6, height: 6, borderRadius: "50%", background: T.green }} />
                  <span>
                    Measuring <b style={{ color: "#fff" }}>{coverage.stations_measured_now}</b>
                    {" "}of <b style={{ color: "#fff" }}>{coverage.stations_known}</b>
                    {" "}tracked stations · rankings cover measured only
                  </span>
                </Link>
              )}
              {stats?.as_of && (
                <span>Updated {new Date(stats.as_of).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit", timeZone: "UTC" })} GMT</span>
              )}
              {stats?.scope === "global" && (
                <span style={{ color: T.green }}>{stats.note}</span>
              )}
            </div>
          </div>
        </div>
      </section>

      {/* ────────────────────────────────────────────────────────────────
          1.5 GET-MEASURED BANNER — highest-leverage lever for growing
          coverage beyond the ~10 stations whose Icecast is public. Any
          NCA-licensed operator can host with us for free (Streams) and
          instantly become DIRECT-measured, or connect their existing
          Zeno/StreamGuys stats via the wizard.
         ──────────────────────────────────────────────────────────────── */}
      <section data-testid="get-measured-banner" style={{
        maxWidth: 1180, margin: "0 auto", padding: "0 24px",
      }}>
        <div style={{
          marginTop: 48,
          padding: "22px 28px",
          borderRadius: 14,
          background: `linear-gradient(135deg, ${T.green}18 0%, ${T.green}08 100%)`,
          border: `1px solid ${T.green}44`,
          display: "flex", alignItems: "center", justifyContent: "space-between",
          gap: 20, flexWrap: "wrap",
        }}>
          <div style={{ flex: "1 1 480px", minWidth: 0 }}>
            <div style={{
              fontSize: 11, fontWeight: 700, color: T.green,
              letterSpacing: "0.14em", textTransform: "uppercase", marginBottom: 6,
            }}>
              Run a station?
            </div>
            <div style={{
              fontSize: 18, fontWeight: 700, color: T.navy,
              lineHeight: 1.35, marginBottom: 6,
            }}>
              Get measured free — join the DIRECT-published list on Ghana&apos;s only digital radio ranking.
            </div>
            <div style={{ fontSize: 13, color: T.muted, lineHeight: 1.55 }}>
              Two paths: <b>host with WaveScore Streams</b> (free NCA-gated Icecast) or{" "}
              <b>connect your existing stats</b> from Zeno, StreamGuys, or any provider.
              Founding stations lock in a 50% Phase-2 discount.
            </div>
          </div>
          <div style={{ display: "flex", gap: 10, flexWrap: "wrap", flexShrink: 0 }}>
            <Link
              to="/streams"
              data-testid="get-measured-primary"
              onClick={() => trackClick("landing.get_measured.primary")}
              style={{
                padding: "12px 22px", borderRadius: 999,
                background: T.green, color: "#fff",
                textDecoration: "none", fontSize: 14, fontWeight: 700,
                display: "inline-flex", alignItems: "center", gap: 6,
                whiteSpace: "nowrap",
              }}
            >
              Get measured →
            </Link>
            <Link
              to="/pricing"
              data-testid="get-measured-secondary"
              onClick={() => trackClick("landing.get_measured.secondary")}
              style={{
                padding: "12px 20px", borderRadius: 999,
                background: "transparent", color: T.navy,
                border: `1px solid ${T.navy}33`,
                textDecoration: "none", fontSize: 13, fontWeight: 600,
                whiteSpace: "nowrap",
              }}
            >
              See what&apos;s included
            </Link>
          </div>
        </div>
      </section>

      {/* ────────────────────────────────────────────────────────────────
          2. LIVE PICKER — 6 stations, tap-to-play
         ──────────────────────────────────────────────────────────────── */}
      <section id="live-picker" data-testid="live-picker" style={{
        maxWidth: 1180, margin: "0 auto", padding: "84px 24px 56px",
      }}>
        <div style={{
          display: "flex", justifyContent: "space-between", alignItems: "baseline",
          flexWrap: "wrap", gap: 12, marginBottom: 32,
        }}>
          <div>
            <div style={{ fontSize: 11, fontWeight: 700, color: T.green,
              letterSpacing: "0.14em", textTransform: "uppercase", marginBottom: 8 }}>
              Listen now
            </div>
            <h2 style={{ fontSize: "clamp(28px,3.4vw,42px)", fontWeight: 700,
              letterSpacing: "-0.02em", margin: 0, color: T.navy }}>
              Ghana&apos;s top stations, live.
            </h2>
          </div>
          <Link to="/listen" data-testid="picker-see-all" style={{
            color: T.green, fontSize: 14, fontWeight: 700, textDecoration: "none",
            borderBottom: `1px solid ${T.green}55`, paddingBottom: 2,
          }}>Explore all stations →</Link>
        </div>

        <div data-testid="picker-grid" style={{
          display: "grid",
          gridTemplateColumns: "repeat(auto-fill, minmax(280px, 1fr))",
          gap: 16,
        }}>
          {pickerStations.length === 0 && (
            Array.from({ length: 6 }).map((_, i) => (
              <div key={i} style={{ height: 96, borderRadius: 12,
                background: "#F1F5F9", animation: "wsTick 1.4s ease-in-out infinite alternate" }} />
            ))
          )}
          {pickerStations.map((s) => (
            <PickerCard key={s.id} s={s} player={player} />
          ))}
        </div>
      </section>

      {/* ────────────────────────────────────────────────────────────────
          3. TRENDING STRIP — top-5 in country, minimal bars
         ──────────────────────────────────────────────────────────────── */}
      <section data-testid="trending-strip" style={{
        background: "#F8FAFC", borderTop: `1px solid ${T.border}`,
        borderBottom: `1px solid ${T.border}`, padding: "80px 24px",
      }}>
        <div style={{ maxWidth: 1180, margin: "0 auto",
          display: "grid", gridTemplateColumns: "minmax(0, 1fr)", gap: 32 }}>
          <div style={{ display: "flex", justifyContent: "space-between",
            alignItems: "baseline", flexWrap: "wrap", gap: 12 }}>
            <div>
              <div style={{ fontSize: 11, fontWeight: 700, color: T.green,
                letterSpacing: "0.14em", textTransform: "uppercase", marginBottom: 8 }}>
                Trending this hour
              </div>
              <h2 style={{ fontSize: "clamp(24px,3vw,36px)", fontWeight: 700,
                letterSpacing: "-0.02em", margin: 0, color: T.navy }}>
                Most-listened stations in {country}.
              </h2>
            </div>
            <div style={{ fontSize: 12, color: T.muted }}>
              {stats?.as_of ? `Updated ${new Date(stats.as_of).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit", timeZone: "UTC" })} GMT` : "loading…"}
              {" · every count labelled by source"}
            </div>
          </div>

          <div data-testid="trending-bars" style={{
            background: "#fff", borderRadius: 16, border: `1px solid ${T.border}`,
            padding: "8px 8px",
          }}>
            {trending.length === 0 && (
              <div style={{ padding: 30, color: T.muted, fontSize: 14, textAlign: "center" }}>
                Awaiting first live measurement…
              </div>
            )}
            {trending.map((row, i) => {
              const maxL = Math.max(1, ...trending.map((r) => r.listeners_now));
              const pct = Math.max(4, Math.round((row.listeners_now / maxL) * 100));
              return (
                <div key={row.station_id} data-testid={`trending-row-${i}`}
                  role="button" tabIndex={0}
                  onClick={() => player?.toggle({ id: row.station_id, name: row.name, frequency: row.frequency })}
                  className="ws-hover-lift"
                  style={{
                    display: "grid",
                    gridTemplateColumns: "36px 1fr auto",
                    alignItems: "center", gap: 16,
                    padding: "18px 20px", borderRadius: 12, cursor: "pointer",
                    borderBottom: i < trending.length - 1 ? `1px solid ${T.border}` : "none",
                  }}>
                  <div className="ws-num" style={{ fontSize: 22, fontWeight: 800, color: T.muted }}>
                    {String(row.rank).padStart(2, "0")}
                  </div>
                  <div style={{ minWidth: 0 }}>
                    <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 8 }}>
                      <span style={{ fontWeight: 700, color: T.navy, fontSize: 15,
                        whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                        {row.name}
                      </span>
                      <span style={{ fontSize: 11, color: T.muted }}>{row.frequency}</span>
                      <span data-testid={`trending-badge-${i}`}
                        title={labelFor(row.confidence_label).helper}
                        aria-label={labelFor(row.confidence_label).label}
                        style={{
                        fontSize: 10, fontWeight: 800, padding: "2px 8px", borderRadius: 4,
                        // F-115 — colour palette derived from labels.TONE_COLORS
                        // but rendered as light chip on the white Landing hero.
                        background: (() => {
                          const t = labelFor(row.confidence_label).tone;
                          return t === "success" ? "#DCFCE7"
                               : t === "info"    ? "#DBEAFE"
                               : t === "warning" ? "#FEF3C7"
                               : "#E5E7EB";
                        })(),
                        color: (() => {
                          const t = labelFor(row.confidence_label).tone;
                          return t === "success" ? "#065F46"
                               : t === "info"    ? "#1E40AF"
                               : t === "warning" ? "#92400E"
                               : "#334155";
                        })(),
                        letterSpacing: "0.06em",
                      }}>
                        {labelFor(row.confidence_label).label}
                      </span>
                    </div>
                    <div style={{ height: 6, borderRadius: 999,
                      background: "#F1F5F9", overflow: "hidden" }}>
                      <div style={{ height: "100%", width: `${pct}%`, background: T.green,
                        borderRadius: 999, transition: "width .6s ease-out" }} />
                    </div>
                  </div>
                  <div style={{ textAlign: "right", minWidth: 140 }}>
                    <div className="ws-num" style={{ fontSize: 20, fontWeight: 800, color: T.navy }}>
                      {row.listeners_now.toLocaleString()}
                    </div>
                    <div style={{ fontSize: 11, color: T.muted }}>
                      listeners · press to play
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* ────────────────────────────────────────────────────────────────
          3b. YOUTUBE TRENDING — "What Ghana is playing right now"
          Subtle carousel — auto-hides if the API returns nothing.
         ──────────────────────────────────────────────────────────────── */}
      {ytVideos.length > 0 && (
        <section data-testid="youtube-carousel" style={{
          maxWidth: 1180, margin: "0 auto", padding: "80px 24px 40px",
        }}>
          <div style={{ display: "flex", justifyContent: "space-between",
            alignItems: "baseline", flexWrap: "wrap", gap: 12, marginBottom: 28 }}>
            <div>
              <div style={{ fontSize: 11, fontWeight: 700, color: T.green,
                letterSpacing: "0.14em", textTransform: "uppercase", marginBottom: 8 }}>
                On YouTube in Ghana
              </div>
              <h2 style={{ fontSize: "clamp(22px,2.6vw,32px)", fontWeight: 700,
                letterSpacing: "-0.02em", margin: 0, color: T.navy }}>
                What Ghana is playing right now.
              </h2>
            </div>
            <a href="https://www.youtube.com/feed/trending" target="_blank" rel="noopener noreferrer"
              data-testid="youtube-see-all"
              style={{ color: T.muted, fontSize: 13, textDecoration: "none",
                borderBottom: `1px solid ${T.border}`, paddingBottom: 2 }}>
              See more on YouTube →
            </a>
          </div>
          <div data-testid="youtube-strip" style={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))",
            gap: 16,
          }}>
            {ytVideos.slice(0, 5).map((v, i) => (
              <a key={v.id} href={v.url} target="_blank" rel="noopener noreferrer"
                data-testid={`youtube-card-${i}`}
                className="ws-hover-lift"
                style={{ background: "#fff", borderRadius: 12,
                  overflow: "hidden", border: `1px solid ${T.border}`,
                  textDecoration: "none", color: "inherit",
                  display: "block" }}>
                {v.thumbnail && (
                  <div style={{ aspectRatio: "16/9", overflow: "hidden",
                    background: "#F1F5F9" }}>
                    <img src={v.thumbnail} alt="" loading="lazy"
                      style={{ width: "100%", height: "100%", objectFit: "cover" }} />
                  </div>
                )}
                <div style={{ padding: "12px 14px" }}>
                  <div style={{ fontSize: 13, fontWeight: 700, color: T.navy,
                    lineHeight: 1.35, marginBottom: 6,
                    display: "-webkit-box", WebkitLineClamp: 2,
                    WebkitBoxOrient: "vertical", overflow: "hidden" }}>
                    {v.title}
                  </div>
                  <div style={{ fontSize: 11, color: T.muted,
                    display: "flex", justifyContent: "space-between", gap: 8 }}>
                    <span style={{ whiteSpace: "nowrap", overflow: "hidden",
                      textOverflow: "ellipsis" }}>{v.channel}</span>
                    <span className="ws-num" style={{ flexShrink: 0 }}>
                      {v.views >= 1_000_000 ? `${(v.views/1_000_000).toFixed(1)}M`
                        : v.views >= 1_000 ? `${(v.views/1_000).toFixed(0)}K`
                        : v.views}
                    </span>
                  </div>
                </div>
              </a>
            ))}
          </div>
        </section>
      )}

      {/* ────────────────────────────────────────────────────────────────
          4. SUBSCRIBE BAND — single conversion CTA
         ──────────────────────────────────────────────────────────────── */}
      <section data-testid="subscribe-band" style={{ padding: "96px 24px" }}>
        <div style={{
          maxWidth: 980, margin: "0 auto",
          background: `linear-gradient(135deg, ${T.navy} 0%, #142842 100%)`,
          borderRadius: 24, padding: "56px 48px",
          display: "grid", gridTemplateColumns: "minmax(0, 1fr) auto",
          gap: 24, alignItems: "center", color: "#fff",
        }}>
          <div>
            <div style={{ fontSize: 11, fontWeight: 700, color: T.green,
              letterSpacing: "0.14em", textTransform: "uppercase", marginBottom: 10 }}>
              For broadcasters & advertisers
            </div>
            <h2 style={{ fontSize: "clamp(24px, 3vw, 32px)", fontWeight: 700,
              letterSpacing: "-0.02em", margin: "0 0 10px", color: "#fff" }}>
              Own a station? Run campaigns? Sign up free.
            </h2>
            <p style={{ margin: 0, fontSize: 15, color: "rgba(255,255,255,0.72)", lineHeight: 1.6, maxWidth: 560 }}>
              Live digital connection counts today · tamper-evident ad-verification ·
              demographic splits arriving with Phase 2.
              Zero setup — Ghana&apos;s stations already measured.
            </p>
          </div>
          <div style={{ display: "flex", gap: 12, flexWrap: "wrap", justifyContent: "flex-end" }}>
            <button data-testid="subscribe-cta-register"
              onClick={() => nav("/register")}
              style={{
                background: T.green, color: "#fff", border: "none",
                padding: "16px 28px", borderRadius: 999, cursor: "pointer",
                fontSize: 15, fontWeight: 700, fontFamily: "inherit",
                whiteSpace: "nowrap",
              }}>
              Get started free
            </button>
            <Link data-testid="subscribe-see-product" to="/product"
              style={{
                border: "1px solid rgba(255,255,255,0.28)",
                padding: "16px 28px", borderRadius: 999,
                fontSize: 15, fontWeight: 600, color: "#fff",
                textDecoration: "none", whiteSpace: "nowrap",
              }}>
              See the product →
            </Link>
          </div>
        </div>
      </section>
      </div>

      {/* ────────────────────────────────────────────────────────────────
          5. FOOTER — updated legal entity (I-Assist Pro Limited)
         ──────────────────────────────────────────────────────────────── */}
      <footer style={{
        background: T.navy, color: "rgba(255,255,255,0.65)",
        padding: "48px 24px 36px",
      }}>
        <div style={{
          maxWidth: 1180, margin: "0 auto",
          display: "grid", gridTemplateColumns: "1fr auto", gap: 24,
          alignItems: "flex-start", flexWrap: "wrap",
        }}>
          <div>
            <div style={{ display: "inline-flex", alignItems: "center", gap: 10,
              color: "#fff", fontWeight: 800, fontSize: 18, marginBottom: 12 }}>
              <WaveMark size={28} inverted />
              WaveScore<span style={{ color: T.green }}>Pro</span>
            </div>
            <p style={{ fontSize: 13, lineHeight: 1.6, margin: "0 0 8px",
              maxWidth: 480, color: "rgba(255,255,255,0.65)" }}>
              Ghana&apos;s real-time audience-measurement platform. Live digital
              connection counts + independent ad-verification for radio &amp; TV.
              Demographics arriving with Phase 2.
            </p>
            <div data-testid="footer-legal-entity" style={{
              fontSize: 12, color: "rgba(255,255,255,0.55)",
              marginTop: 14, lineHeight: 1.6,
            }}>
              © 2026 <b style={{ color: "rgba(255,255,255,0.85)" }}>I-Assist Pro Limited</b> ·
              {" "}Accra, Ghana ·{" "}
              <a href="tel:+233208553679" style={{ color: "rgba(255,255,255,0.85)", textDecoration: "none" }}>
                +233 20 855 3679
              </a>
            </div>
          </div>
          <nav data-testid="footer-links" style={{
            display: "grid", gridTemplateColumns: "repeat(2, minmax(120px, 1fr))",
            columnGap: 40, rowGap: 8, fontSize: 13,
          }}>
            <Link data-testid="footer-listen" to="/listen" style={{ color: "rgba(255,255,255,0.75)", textDecoration: "none" }}>Listen live</Link>
            <Link data-testid="footer-product" to="/product" style={{ color: "rgba(255,255,255,0.75)", textDecoration: "none" }}>Product</Link>
            <Link data-testid="footer-pricing" to="/pricing" style={{ color: "rgba(255,255,255,0.75)", textDecoration: "none" }}>Pricing</Link>
            <Link data-testid="footer-blog" to="/blog" style={{ color: "rgba(255,255,255,0.75)", textDecoration: "none" }}>Blog</Link>
            <Link data-testid="footer-methodology" to="/methodology" style={{ color: "rgba(255,255,255,0.75)", textDecoration: "none" }}>Methodology</Link>
            <Link data-testid="footer-verified" to="/verified" style={{ color: "rgba(255,255,255,0.75)", textDecoration: "none" }}>Verified charts</Link>
            <Link data-testid="footer-terms" to="/terms" style={{ color: "rgba(255,255,255,0.75)", textDecoration: "none" }}>Terms</Link>
            <Link data-testid="footer-privacy" to="/privacy" style={{ color: "rgba(255,255,255,0.75)", textDecoration: "none" }}>Privacy</Link>
          </nav>
        </div>
      </footer>
    </div>
  );
}

function PickerCard({ s, player }) {
  const isPlaying = player?.playingId === s.id;
  return (
    <button
      data-testid={`picker-card-${s.id}`}
      onClick={() => player?.toggle({ id: s.id, name: s.name, frequency: s.frequency, logo_url: s.logo_url })}
      className="ws-hover-lift"
      style={{
        display: "grid", gridTemplateColumns: "1fr auto", gap: 12, alignItems: "center",
        background: "#fff",
        border: `1px solid ${isPlaying ? T.green : T.border}`,
        boxShadow: isPlaying ? `0 8px 24px ${T.green}22` : "0 1px 2px rgba(10,22,40,.04)",
        borderRadius: 14, padding: "18px 20px", textAlign: "left",
        cursor: "pointer", fontFamily: "inherit",
      }}>
      <div style={{ minWidth: 0 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 4 }}>
          <span style={{ fontWeight: 700, color: T.navy, fontSize: 16,
            whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
            {s.name}
          </span>
          {s.is_youtube_live && (
            <span data-testid={`picker-yt-badge-${s.id}`} style={{
              fontSize: 9, fontWeight: 800, color: "#fff", background: "#DC2626",
              padding: "2px 6px", borderRadius: 3, letterSpacing: "0.06em",
              flexShrink: 0,
            }}>YT LIVE</span>
          )}
        </div>
        <div style={{ fontSize: 12, color: T.muted }}>
          {s.frequency} · {s.city || "Ghana"}{" "}
          {s.listeners > 0 && (
            <>· <b className="ws-num" style={{ color: T.green }}>{s.listeners.toLocaleString()}</b> listening</>
          )}
        </div>
      </div>
      <div style={{
        width: 44, height: 44, borderRadius: "50%",
        background: isPlaying ? T.navy : T.green, color: "#fff",
        display: "inline-flex", alignItems: "center", justifyContent: "center",
        fontSize: 14, fontWeight: 800, flexShrink: 0,
      }}>
        {isPlaying ? "❚❚" : "▶"}
      </div>
    </button>
  );
}
